// ANGULAR
import { Component, DestroyRef, Inject, computed, inject } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';

// ANGULAR MATERIAL
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

// RxJS 6
import { tap } from 'rxjs/operators';

// API
import {
  IWHFlowContactDTO,
  WHIconENUM,
  WHTranslationTypeENUM,
  Country,
  countryCodes,
} from '@workheld/workheld-shared-lib';
import { FormReferenceService } from 'src/app/app-services-helper/form-reference.service';

import { initContactFormGroup } from 'src/app/app-services/form.service';

@Component({
  selector: 'mat-dialog-contact-entry',
  templateUrl: './mat-dialog-contact-entry.component.html',
  styleUrls: ['./mat-dialog-contact-entry.component.scss'],
  standalone: false,
})
export class MatDialogContactEntryComponent {
  contactDTO: IWHFlowContactDTO;
  contactFormGroup: FormGroup = initContactFormGroup();
  sortedCountryCodes: Country[] = [...countryCodes];
  private readonly destroyRef = inject(DestroyRef);
  private readonly formValue = toSignal(this.contactFormGroup.valueChanges, {
    initialValue: this.contactFormGroup.value,
  });
  private readonly formStatus = toSignal(this.contactFormGroup.statusChanges, {
    initialValue: this.contactFormGroup.status,
  });
  ENUMS = WHTranslationTypeENUM;
  ICONS = WHIconENUM;

  readonly firstname = computed(() => this.formValue().firstname as string);
  readonly lastname = computed(() => this.formValue().lastname as string);
  readonly contactFormGroupDisabled = computed(() => {
    this.formStatus();
    return this.contactFormGroup.disabled;
  });
  readonly contactFormGroupInvalid = computed(() => {
    this.formStatus();
    return this.contactFormGroup.invalid;
  });

  constructor(
    public matDialogRef: MatDialogRef<MatDialogContactEntryComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      contactDTO: IWHFlowContactDTO;
    },
    private readonly formReferenceService: FormReferenceService,
  ) {
    this.sortedCountryCodes.sort((a, b) => {
      const A = Number.parseInt(a.dial_code.replace(/\D/g, ''));
      const B = Number.parseInt(b.dial_code.replace(/\D/g, ''));
      return A - B;
    });
    if (this.data.contactDTO) {
      this.contactDTO = this.data.contactDTO;
      this.contactFormGroup.patchValue(this.data.contactDTO);
      this.contactFormGroup.controls['landLinePhone'].markAllAsTouched();
      this.contactFormGroup.controls['mobilePhone'].markAllAsTouched();
    }

    this.contactFormGroup.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        this.validatePhone(value);
        this.validateMobilePhone(value);
      });
  }

  private validatePhone(value: any) {
    if (value.countryCodeLandLine || value.landLinePhone) {
      if (!value.countryCodeLandLine || !value.landLinePhone) {
        this.contactFormGroup.setErrors({ invalidPhone: true });
      } else if (!/^\+(\d+)$/g.test(value.countryCodeLandLine)) {
        this.contactFormGroup.setErrors({ invalidMobilePhone: true });
      } else if (
        value.countryCodeLandLine &&
        value.landLinePhone &&
        this.contactFormGroup.errors?.invalidPhone
      ) {
        this.contactFormGroup.setErrors({ invalidPhone: null });
        this.contactFormGroup.updateValueAndValidity();
      }
    }
  }

  private validateMobilePhone(value) {
    if (value.countryCodeMobile || value.mobilePhone) {
      if (!value.countryCodeMobile || !value.mobilePhone) {
        this.contactFormGroup.setErrors({ invalidMobilePhone: true });
      } else if (!/^\+(\d+)$/g.test(value.countryCodeMobile)) {
        this.contactFormGroup.setErrors({ invalidMobilePhone: true });
      } else if (
        value.countryCodeMobile &&
        value.mobilePhone &&
        this.contactFormGroup.errors?.mobilePhone
      ) {
        this.contactFormGroup.setErrors({ invalidMobilePhone: null });
        this.contactFormGroup.updateValueAndValidity();
      }
    }
  }

  public handleSubmit(): void {
    if (this.contactFormGroup.invalid) {
      return;
    }
    this.matDialogRef.close(this.contactFormGroup.value);
  }

  public deleteContact(): void {
    const contactId = this.contactFormGroup.controls['id'].value;
    this.matDialogRef.close(contactId);
  }

  public closeDialog() {
    if (this.contactFormGroup.dirty) {
      const ref = this.formReferenceService.createDialog();
      ref
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          tap((canDiscard) => {
            if (canDiscard) {
              this.matDialogRef.close();
            }
          }),
        )
        .subscribe();
    } else {
      this.matDialogRef.close();
    }
  }
}
